import React, {useEffect, useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {LocationMarkerIcon, OfficeBuildingIcon, WifiIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {useNavigate, useSearchParams} from "react-router-dom";
import DeleteInteractionArea from '../Delete';
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {InteractionArea} from "../../../types/InteractionArea";
import InteractionAreaContextMenu from "../../../components/menus/InteractionAreaContextMenu";
import FloatingDropdown from "../../../components/FloatingDropdown";
import {useOperator} from "../../../context/OperatorContext";
import {getStoredItem, setStoredItem, STORAGE_KEYS} from "../../../services/storage";
import {Site} from "../../../types/Site";
import {isValidSiteId} from "../../../services/site";

// Types ---

type SiteItem = {
  label: string
  value: string | null
}

// Component ----

function InteractionAreaList() {
  const auth = useAuth();
  const navigate = useNavigate();
  const {termFor} = useOperator();
  const [searchParams, setSearchParams] = useSearchParams({
    site: '',
  });

  const [deletingInteractionArea, setDeletingInteractionArea] = useState<InteractionArea | null>(null);
  const [page, setPage] = useState<number>(1);
  const siteIdParam = searchParams.get('site') ?? '';

  const {data: sites, isLoading: sitesLoading} = useQuery(['sites.all'], () => ngage.sites.all());

  const result = useQuery(
    ['interaction-areas.all', {page, sites: siteIdParam}],
    () => ngage.interactionAreas.filtered({page, sites: siteIdParam}),
    {keepPreviousData: true, enabled: auth.can('interaction-areas:read')}
  )

  // Prefetch next and previous pages
  prefetchNearbyPages('interaction-areas.all', page, ngage.interactionAreas.all);

  // Check URL params or local storage for selected site id when sites are loaded
  useEffect(() => {
    if (!sites?.data) return;

    const storedSiteId = getStoredItem(STORAGE_KEYS.INTERACTION_AREAS_SITE_ID);
    const defaultSiteId = storedSiteId ?? '';
    const siteId = isValidSiteId(sites.data, siteIdParam) ? siteIdParam : defaultSiteId;

    setSearchParams(currentParams => {
      currentParams.set('site', siteId);
      return currentParams;
    }, { replace: true });

  }, [siteIdParam, sites?.data, setSearchParams])

  // Prevent unauthorised viewing
  if (auth.cannot('interaction-areas:read')) {
    return <NotAuthorised/>
  }

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    const allSites: SiteItem = {label: `All ${termFor('sites')}`, value: null};
    const dynamicTypes = sites.map(site => ({label: site.name, value: site.id.toString()}));

    return [allSites, ...dynamicTypes];
  }

  // Get site items for dropdown
  const siteItems = sites?.data ? getSiteItems(sites.data) : [];

  // Set selected site
  function selectSite(siteId: number | null) {
    const siteIdString = siteId?.toString() ?? '';
    setSearchParams(currentParams => {
      currentParams.set('site', siteIdString);
      return currentParams;
    }, { replace: true });
    setStoredItem(STORAGE_KEYS.INTERACTION_AREAS_SITE_ID, siteIdString);
  }

  // Prepare page actions
  const actions = (
    <>
      {auth.can('interaction-areas:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/interaction-areas/new')}>New</Button>
      }
    </>
  )

  // Prepare breadcrumbs
  const breadcrumbs = {
    'Operations': '/', 'Interaction Areas': '/interaction-areas'
  }

  return (
    <Page
      title='Interaction Areas'
      subTitle={`Customisable location areas for interactive engagement`}
      actions={actions}
      breadcrumbs={breadcrumbs}
      isLoading={result.isLoading || sitesLoading}
    >
      <div className="flex flex-col gap-5">
        <header className='flex justify-between gap-2 items-end'>
          {/* Refresh button */}
          <Button
            onClick={() => result.refetch()}
            showSpinner={result.isFetching}
            disabled={result.isFetching}
            size={Button.sizes.TINY}
            color={Button.colors.SECONDARY}
          >
            {result.isFetching ? 'Refreshing' : 'Refresh'}
          </Button>

          <div className='flex gap-2'>
            {/* Site filter */}
            <FloatingDropdown
              label={termFor('Site')}
              items={siteItems}
              selectedIndex={Math.max(siteItems.findIndex((item) => item.value === siteIdParam), 0)}
              onSelect={(item) => selectSite(item.value)}
            />
          </div>
        </header>

        <Table paginationData={result.data} setPage={setPage}>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Site</TableHeader>
              <TableHeader>Added</TableHeader>
              <TableHeader/>
            </TableRow>
          </TableHead>

          <TableBody isLoading={result.isLoading}>
            {result?.data && result.data.data.length < 1 ?
              <TableRow index={0}>
                <TableData colSpan={9}>
                  <div className="py-4 flex flex-col justify-center items-center">
                    <LocationMarkerIcon className="h-8 w-8 text-gray-400 mb-2"/>
                    <span className="text-sm font-medium text-gray-600">No Interaction Areas</span>
                  </div>
                </TableData>
              </TableRow>
              : result?.data?.data.map((interactionArea, index) => (
                <TableRow key={interactionArea.id} index={index} highlight>
                  <TableData>
                    <div className="flex">
                      <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                        <InteractionAreaContextMenu
                          interactionArea={interactionArea}
                          onDeleteInteractionArea={() => setDeletingInteractionArea(interactionArea)}
                          deleteDisabled={interactionArea.trigger_count > 0}
                          position="left"
                        />
                      </div>

                      <div className="flex group-hover:translate-x-1 transition-all">
                        <LocationMarkerIcon className="h-5 w-5 mr-2 text-gray-500"/> {interactionArea.name}
                      </div>
                    </div>
                  </TableData>
                  <TableData>{interactionArea.description}</TableData>
                  <TableData>{interactionArea.site?.name}</TableData>
                  <TableData>{formattedDateTime(interactionArea.created_at)}</TableData>
                  <TableData alignment={TableData.alignments.RIGHT}>
                    <InteractionAreaContextMenu
                      interactionArea={interactionArea}
                      onDeleteInteractionArea={() => setDeletingInteractionArea(interactionArea)}
                      deleteDisabled={interactionArea.trigger_count > 0}
                    />
                  </TableData>

                  {/* Delete confirmation modal */}
                  <DeleteInteractionArea
                    interactionArea={deletingInteractionArea}
                    show={interactionArea === deletingInteractionArea}
                    onCancel={() => setDeletingInteractionArea(null)}
                    onDelete={() => navigate('/interaction-areas')}
                    onDeleteFail={() => setDeletingInteractionArea(null)}
                  />
                </TableRow>
              ))}

          </TableBody>
        </Table>
      </div>
    </Page>
  );
}

export default InteractionAreaList;